import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Typography,
} from '@crossnokaye/ux-common';
import { RadioButton, RadioGroup } from '@crossnokaye/ux-common/ckds';

import { TableColumnConfig, TableRowConfig } from '@repo/sdk/models/components';

import { PreviewSection } from '@/components/settings/preview-section';
import { Button } from '@/ux-common/components/button/button';
import { Stack } from '@/ux-common/components/stack/stack';

import { formatCellValue } from '../equipment-bulk-edit.helpers';
import type { DriftedSetting, EquipmentBulkEditFormValues } from '../equipment-bulk-edit.types';

/** Which value the user selected for a drifted setting */
type SelectionChoice = 'edit' | 'live';

interface DriftedSettingGroup {
  rowId: string;
  rowName: string;
  settings: DriftedSettingDisplay[];
}

interface DriftedSettingDisplay {
  key: string;
  rowId: string;
  columnId: string;
  columnLabel: string;
  displayEdit: string;
  displayLive: string;
  rawEditValue: string;
  rawLiveValue: string;
}

interface SyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driftedSettings: DriftedSetting[];
  columns: TableColumnConfig[];
  rows: TableRowConfig[];
  onClose: () => void;
  onContinue: () => void;
}

/**
 * SyncDialog
 *
 * Shown when the user clicks "Review & Save" and a drift check detects that
 * one or more settings have been changed on the server since the form was loaded.
 * Displays conflicts grouped by device with radio buttons to choose between
 * the user's edit and the live server value. On "Continue", applies the selected
 * values to the form and transitions to the SaveChangesDialog.
 */
export const SyncDialog = ({
  open,
  onOpenChange,
  driftedSettings,
  columns,
  rows,
  onClose,
  onContinue,
}: SyncDialogProps): React.ReactNode => {
  const { setValue, getValues } = useFormContext<{ values: EquipmentBulkEditFormValues }>();

  // Build display data for all drifted settings
  const rowMap = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const columnMap = useMemo(() => new Map(columns.map((c) => [c.columnId, c])), [columns]);

  const displayItems = useMemo((): DriftedSettingDisplay[] => {
    const currentValues = getValues('values');
    return driftedSettings.map((ds) => {
      const key = `${ds.rowId}-${ds.columnId}`;
      const row = rowMap.get(ds.rowId);
      const column = columnMap.get(ds.columnId);
      const rawEditValue = currentValues?.[ds.rowId]?.[ds.columnId] ?? '';
      const rawLiveValue = ds.serverValue;

      return {
        key,
        rowId: ds.rowId,
        columnId: ds.columnId,
        columnLabel: column?.label ?? ds.columnId,
        displayEdit: row
          ? formatCellValue(rawEditValue, ds.columnId, row, { booleanLabelCase: 'upper' })
          : rawEditValue,
        displayLive: row
          ? formatCellValue(rawLiveValue, ds.columnId, row, { booleanLabelCase: 'upper' })
          : rawLiveValue,
        rawEditValue,
        rawLiveValue,
      };
    });
  }, [driftedSettings, rowMap, columnMap, getValues]);

  // Group display items by equipment (row)
  const groups = useMemo((): DriftedSettingGroup[] => {
    const map = new Map<string, DriftedSettingGroup>();
    for (const item of displayItems) {
      const existing = map.get(item.rowId);
      if (existing) {
        existing.settings.push(item);
      } else {
        const row = rowMap.get(item.rowId);
        map.set(item.rowId, {
          rowId: item.rowId,
          rowName: row?.name ?? item.rowId,
          settings: [item],
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.rowName.localeCompare(b.rowName, undefined, { numeric: true })
    );
  }, [displayItems, rowMap]);

  // Track which value is selected for each drifted setting (default: user's edit)
  const [selections, setSelections] = useState<Record<string, SelectionChoice>>({});

  // Reset selections when dialog opens or drifted settings change
  useEffect(() => {
    if (open) {
      const initial: Record<string, SelectionChoice> = {};
      for (const item of displayItems) {
        initial[item.key] = 'edit';
      }
      setSelections(initial);
    }
  }, [open, displayItems]);

  const handleSelectAll = useCallback((choice: SelectionChoice) => {
    setSelections((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = choice;
      }
      return next;
    });
  }, []);

  const handleSelectOne = useCallback((key: string, choice: SelectionChoice) => {
    setSelections((prev) => ({ ...prev, [key]: choice }));
  }, []);

  // Derive the global selection state for the top-level radio group
  const globalSelection = useMemo((): SelectionChoice | 'mixed' => {
    const values = Object.values(selections);
    if (values.length === 0) return 'edit';
    const allEdit = values.every((v) => v === 'edit');
    const allLive = values.every((v) => v === 'live');
    if (allEdit) return 'edit';
    if (allLive) return 'live';
    return 'mixed';
  }, [selections]);

  const handleContinue = useCallback(() => {
    // Apply selected values to the form
    for (const item of displayItems) {
      const choice = selections[item.key] ?? 'edit';
      if (choice === 'live') {
        setValue(`values.${item.rowId}.${item.columnId}`, item.rawLiveValue, {
          shouldDirty: true,
          shouldTouch: false,
        });
      }
      // 'edit' means keep the current form value — no action needed
    }
    onContinue();
  }, [displayItems, selections, setValue, onContinue]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="text-foreground sm:max-w-[640px] bg-ckds-neutral-900 min-h-[30vh] max-h-[80vh] flex flex-col p-thin sm:p-stack-default">
        <DialogHeader className="flex-shrink-0 items-start pt-stack-sm px-stack-sm sm:p-none">
          <DialogTitle>Sync Conflict Detected</DialogTitle>
        </DialogHeader>

        <Typography variant="body" className="text-ckds-text-fg-tier2 px-stack-sm sm:px-none">
          Some values changed on the cloud while you were editing. Choose which values to keep.
        </Typography>

        {/* Global select-all radio group */}
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2 px-2 items-center">
          <div />
          <RadioGroup
            orientation="horizontal"
            value={globalSelection === 'mixed' ? undefined : globalSelection}
            onValueChange={(val: string) => handleSelectAll(val as SelectionChoice)}
            className="contents"
          >
            <div className="bg-ckds-neutral-800 rounded px-3 py-2">
              <RadioButton value="edit" label="Select all my edits" />
            </div>
            <div className="bg-ckds-neutral-800 rounded px-3 py-2">
              <RadioButton value="live" label="Select all live values" />
            </div>
          </RadioGroup>
        </div>

        {/* Scrollable conflict list grouped by equipment */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <Stack>
            {groups.map((group) => (
              <PreviewSection key={group.rowId} title={group.rowName}>
                <Stack className="stack-thin">
                  {group.settings.map((setting) => (
                    <SyncConflictRow
                      key={setting.key}
                      setting={setting}
                      selection={selections[setting.key] ?? 'edit'}
                      onSelectionChange={(choice) => handleSelectOne(setting.key, choice)}
                    />
                  ))}
                </Stack>
              </PreviewSection>
            ))}
          </Stack>
        </div>

        <DialogFooter className="flex-shrink-0 flex gap-2 flex-row justify-center sm:justify-end p-stack-sm sm:p-none">
          <Button
            type="button"
            className="btn btn-tier3 sm:w-36 flex-1 sm:flex-none uppercase"
            onClick={onClose}
          >
            Back to Table
          </Button>
          <Button
            type="button"
            className="btn btn-brand flex-1 sm:flex-none"
            onClick={handleContinue}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Per-setting conflict row ────────────────────────────────────────────────

interface SyncConflictRowProps {
  setting: DriftedSettingDisplay;
  selection: SelectionChoice;
  onSelectionChange: (choice: SelectionChoice) => void;
}

const SyncConflictRow = ({
  setting,
  selection,
  onSelectionChange,
}: SyncConflictRowProps): React.ReactElement => {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2 bg-ckds-neutral-800/50 p-2 items-center">
      {/* Setting label */}
      <Typography variant="body" className="text-sm font-semibold uppercase truncate">
        {setting.columnLabel}
      </Typography>

      {/* Your Edit value with radio */}
      <RadioGroup
        orientation="horizontal"
        value={selection}
        onValueChange={(val: string) => onSelectionChange(val as SelectionChoice)}
        className="contents"
      >
        <label className="flex items-center gap-2 cursor-pointer min-w-0">
          <RadioButton
            value="edit"
            className="shrink-0"
            labelClassName="text-ckds-text-fg-tier2"
            label="Your edit"
          />
          <span className="text-sm truncate text-ckds-input-fg-new">{setting.displayEdit}</span>
        </label>

        {/* Live value with radio */}
        <label className="flex items-center gap-2 cursor-pointer min-w-0">
          <RadioButton
            value="live"
            className="shrink-0"
            labelClassName="text-ckds-text-fg-tier2"
            label="Live value"
          />
          <span className="text-sm truncate">{setting.displayLive}</span>
        </label>
      </RadioGroup>
    </div>
  );
};
